<template>
  <div class="rib-property" data-testid="rib-property-section">
    <n-form label-placement="top" size="small">
      <n-form-item label="操作类型">
        <n-select
          :value="operation"
          :options="operationOptions"
          data-testid="rib-operation-select"
          @update:value="handleOperationChange"
        />
      </n-form-item>

      <n-form-item label="厚度类型">
        <n-select
          :value="thicknessType"
          :options="thicknessTypeOptions"
          data-testid="rib-thickness-type-select"
          @update:value="handleThicknessTypeChange"
        />
      </n-form-item>

      <n-form-item label="第一侧厚度 (mm)">
        <n-input-number
          :value="thicknessMm"
          :min="0.001"
          :step="0.5"
          :show-button="true"
          data-testid="rib-thickness-input"
          @update:value="handleThicknessChange"
        />
      </n-form-item>

      <n-form-item v-if="thicknessType === 'twoSides'" label="第二侧厚度 (mm)">
        <n-input-number
          :value="secondThicknessMm"
          :min="0.001"
          :step="0.5"
          :show-button="true"
          data-testid="rib-second-thickness-input"
          @update:value="handleSecondThicknessChange"
        />
      </n-form-item>

      <n-form-item label="实体">
        <div class="rib-summary">
          {{ bodySummary }}
        </div>
      </n-form-item>

      <n-form-item label="轮廓">
        <div class="rib-summary">
          {{ profileSummary }}
        </div>
      </n-form-item>

      <n-form-item label="方向来源">
        <n-select
          :value="directionMode"
          :options="directionModeOptions"
          data-testid="rib-direction-mode-select"
          @update:value="handleDirectionModeChange"
        />
      </n-form-item>

      <n-checkbox
        :checked="enableSliding"
        data-testid="rib-enable-sliding-checkbox"
        @update:checked="handleEnableSlidingChange"
      >
        允许轮廓沿支撑面滑动
      </n-checkbox>

      <n-checkbox
        :checked="reverseMaterialSide"
        data-testid="rib-reverse-material-side-checkbox"
        @update:checked="handleReverseMaterialSideChange"
      >
        反转材料侧
      </n-checkbox>
    </n-form>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef, watch } from 'vue';
import { convertMToMm, convertMmToM } from '@puppycad/cad-core';
import type { RibThicknessType, RibDirectionMode } from '@puppycad/cad-core';
import { useMessage } from 'naive-ui';

/**
 * 筋属性面板。
 *
 * 设计说明：
 * 1. 面板只消费 property-manager 注入的 context，不直接持有筋状态；
 * 2. 底层长度单位是米，UI 显示按 mm，进出都经过 convertMmToM / convertMToMm 收口；
 * 3. 单侧厚度当前不受内核支持，因此厚度类型只开放居中与两侧。
 */

interface RibPropertySectionProps {
    context: {
        ribActive?: boolean;
        ribCanSubmit?: boolean;
        ribBodySelected?: boolean;
        ribPreviewError?: string | null;
        ribDefinition?: {
            thicknessType?: RibThicknessType;
            thickness?: number;
            secondThickness?: number;
            directionMode?: RibDirectionMode;
            operation?: 'rib' | 'groove';
            enableSliding?: boolean;
            reverseMaterialSide?: boolean;
            profile?: {
                points?: unknown[];
            };
        } | null;
        setRibThickness?: (value: number) => void;
        setRibSecondThickness?: (value: number) => void;
        setRibThicknessType?: (value: RibThicknessType) => void;
        setRibDirectionMode?: (value: RibDirectionMode) => void;
        setRibOperation?: (value: 'rib' | 'groove') => void;
        setRibEnableSliding?: (value: boolean) => void;
        setRibReverseMaterialSide?: (value: boolean) => void;
        clearRibPreviewError?: () => void;
    };
}

const props = defineProps<RibPropertySectionProps>();
const context = toRef(props, 'context');
const message = useMessage();

// 预览求解失败时用顶部信息框提示，并清除控制器错误状态以便下一次失败可再次触发。
watch(
    () => context.value.ribPreviewError,
    (error) => {
        if (typeof error !== 'string' || error.length === 0) {
            return;
        }
        message.warning(error);
        context.value.clearRibPreviewError?.();
    },
);

const operationOptions = [
    { label: '筋（加料）', value: 'rib' },
    { label: '槽（减料）', value: 'groove' },
] satisfies Array<{ label: string; value: 'rib' | 'groove' }>;

const thicknessTypeOptions = [
    { label: '居中', value: 'centered' },
    { label: '两侧', value: 'twoSides' },
] satisfies Array<{ label: string; value: RibThicknessType }>;

const directionModeOptions = [
    { label: '草图法向', value: 'sketchNormal' },
    { label: '轮廓法向', value: 'profileNormal' },
] satisfies Array<{ label: string; value: RibDirectionMode }>;

const operation = computed(() => context.value.ribDefinition?.operation ?? 'rib');
const thicknessType = computed<RibThicknessType>(() => context.value.ribDefinition?.thicknessType ?? 'centered');
const directionMode = computed<RibDirectionMode>(() => context.value.ribDefinition?.directionMode ?? 'sketchNormal');
const enableSliding = computed(() => context.value.ribDefinition?.enableSliding !== false);
const reverseMaterialSide = computed(() => context.value.ribDefinition?.reverseMaterialSide === true);

const thicknessMm = computed(() => Number(convertMToMm(
    context.value.ribDefinition?.thickness ?? convertMmToM(2),
).toFixed(3)));

const secondThicknessMm = computed(() => Number(convertMToMm(
    context.value.ribDefinition?.secondThickness ?? convertMmToM(2),
).toFixed(3)));

const bodySummary = computed(() => (
    context.value.ribBodySelected === true
        ? '已选择实体'
        : '点击模型选择实体'
));

const profileSummary = computed(() => {
    const count = context.value.ribDefinition?.profile?.points?.length ?? 0;
    return count > 0 ? `已注入轮廓（${count} 个点）` : '尚未注入轮廓';
});

function handleOperationChange(value: 'rib' | 'groove'): void {
    context.value.setRibOperation?.(value);
}

function handleThicknessTypeChange(value: RibThicknessType): void {
    context.value.setRibThicknessType?.(value);
}

function handleDirectionModeChange(value: RibDirectionMode): void {
    context.value.setRibDirectionMode?.(value);
}

function handleThicknessChange(value: number | null): void {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        return;
    }
    context.value.setRibThickness?.(value);
}

function handleSecondThicknessChange(value: number | null): void {
    if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
        return;
    }
    context.value.setRibSecondThickness?.(value);
}

function handleEnableSlidingChange(checked: boolean): void {
    context.value.setRibEnableSliding?.(checked);
}

function handleReverseMaterialSideChange(checked: boolean): void {
    context.value.setRibReverseMaterialSide?.(checked);
}
</script>

<style scoped>
.rib-summary {
  min-height: 24px;
  line-height: 24px;
  color: var(--n-text-color-2);
}
</style>
